import React, { useEffect, useRef, useState } from 'react';

/**
 * Cloudflare Turnstile widget.
 *
 * Renders the widget, calls `onVerify(token)` when the user passes, and
 * `onExpire()` if the token expires (they'll get a fresh one automatically).
 *
 * Site key comes from `REACT_APP_TURNSTILE_SITE_KEY`. If it isn't set, the
 * widget silently no-ops and calls `onVerify(null)` so dev doesn't get stuck.
 *
 * Get keys: https://dash.cloudflare.com/?to=/:account/turnstile
 */

const SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY;
const SCRIPT_ID = 'cf-turnstile-script';
const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

// Ensures the api.js script is only injected once no matter how many
// widgets are mounted on the page.
function loadTurnstileScript() {
  return new Promise((resolve) => {
    if (window.turnstile) return resolve(window.turnstile);
    const existing = document.getElementById(SCRIPT_ID);
    if (existing) {
      existing.addEventListener('load', () => resolve(window.turnstile));
      return;
    }
    const s = document.createElement('script');
    s.id = SCRIPT_ID;
    s.src = SCRIPT_SRC;
    s.async = true;
    s.defer = true;
    s.onload = () => resolve(window.turnstile);
    document.head.appendChild(s);
  });
}

export default function TurnstileWidget({ onVerify, onExpire, theme = 'dark' }) {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    // If no site key, tell the parent we have no token so submit stays
    // enabled and the backend just enforces its own rate limits.
    if (!SITE_KEY) {
      onVerify?.(null);
      setReady(true);
      return undefined;
    }
    let cancelled = false;
    loadTurnstileScript().then((turnstile) => {
      if (cancelled || !containerRef.current) return;
      widgetIdRef.current = turnstile.render(containerRef.current, {
        sitekey: SITE_KEY,
        theme,
        callback: (token) => onVerify?.(token),
        'expired-callback': () => onExpire?.(),
        'error-callback': () => onExpire?.(),
      });
      setReady(true);
    });
    return () => {
      cancelled = true;
      if (widgetIdRef.current && window.turnstile) {
        try { window.turnstile.remove(widgetIdRef.current); } catch { /* ignore */ }
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!SITE_KEY) return null;

  return (
    <div
      ref={containerRef}
      data-testid="turnstile-widget"
      aria-label="Human verification"
      className={ready ? '' : 'min-h-[65px]'}
    />
  );
}
